import { Component } from '@theme/component';

/**
 * Background video of the Pauta Cero hero.
 *
 * The poster image is the LCP element, so the video is not requested until the page has loaded
 * (`preload="none"` in the markup). It only plays while motion is allowed: it stays on the poster
 * for visitors who prefer reduced motion or have turned on the browser's data saver, while the tab is
 * hidden, or after the visitor pauses it.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} [video] - Absent when the section has no video.
 * @property {HTMLButtonElement} [toggle] - Play/pause control.
 *
 * @extends {Component<Refs>}
 */
class PcHeroVideo extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {MediaQueryList | undefined} */
  #reducedMotion;

  /** Set when the visitor pauses the video; blocks automatic resumes. */
  #pausedByUser = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion.addEventListener('change', this.#sync, { signal });
    document.addEventListener('visibilitychange', this.#sync, { signal });

    if (document.readyState === 'complete') {
      this.#sync();
    } else {
      window.addEventListener('load', this.#sync, { once: true, signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#abort = undefined;
  }

  /**
   * Toggles playback from the visible control.
   */
  togglePlayback() {
    const { video } = this.refs;
    if (!video) return;

    this.#pausedByUser = !video.paused;
    this.#sync();
  }

  /** Plays or pauses the video to match the current motion, visibility and user preference. */
  #sync = () => {
    const { video } = this.refs;
    if (!video) return;

    // Data saver: the clip is background, so it is not worth a few MB to a visitor who asked the browser to save data
    const saveData = /** @type {{ connection?: { saveData?: boolean } }} */ (navigator).connection?.saveData === true;
    const shouldPlay = !this.#reducedMotion?.matches && !document.hidden && !this.#pausedByUser && !saveData;

    if (shouldPlay) {
      if (video.preload === 'none') video.preload = 'auto';

      video
        .play()
        .then(() => this.#render(true))
        .catch(() => this.#render(false));
    } else {
      video.pause();
      this.#render(false);
    }
  };

  /**
   * Reflects the playing state in the markup (video fade-in and control label).
   * @param {boolean} playing
   */
  #render(playing) {
    this.toggleAttribute('data-playing', playing);

    const { toggle } = this.refs;
    if (!toggle) return;

    toggle.setAttribute('aria-pressed', String(!playing));
    toggle.setAttribute('aria-label', (playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay) ?? '');
  }
}

if (!customElements.get('pc-hero-video')) {
  customElements.define('pc-hero-video', PcHeroVideo);
}
